import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PenLine, CheckCircle2, Loader2, ArrowLeft, Printer, UserPlus, MessageSquareQuote, RotateCcw, KeyRound, Award, X, QrCode } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner, SectionLabel, Button, Pill } from "../components/Primitives";

export default function AssignmentDetail() {
  const { slug } = useParams();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState([]);
  const [writing, setWriting] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mySub, setMySub] = useState(null);
  const [retake, setRetake] = useState(false);
  const [learners, setLearners] = useState([]);
  const [learnerSel, setLearnerSel] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [answerKey, setAnswerKey] = useState(null);
  const [printingKey, setPrintingKey] = useState(false);
  const [certOpen, setCertOpen] = useState(false);
  const [certName, setCertName] = useState("");
  const [printingCert, setPrintingCert] = useState(false);
  const [scanCount, setScanCount] = useState(null);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("src") === "qr") {
      api.post("/qr-scan", { kind: "assignment", slug }).catch(() => {});
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [slug]);

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/qr-scans").then((r) => {
      const rec = r.data.find((s) => s.kind === "assignment" && s.slug === slug);
      setScanCount(rec ? rec.count : 0);
    }).catch(() => {});
  }, [user, slug]);

  useEffect(() => {
    api.get(`/coursework/${slug}`).then((r) => {
      setItem(r.data);
      setAnswers(new Array(r.data.questions?.length || 0).fill(""));
    }).catch(() => setItem(null)).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!user) return;
    api.get(`/coursework/${slug}/my-submission`).then((r) => {
      if (r.data && r.data.id) setMySub(r.data);
    }).catch(() => {});
    api.get("/learners").then((r) => setLearners(r.data)).catch(() => {});
  }, [user, slug]);

  const submit = async () => {
    if (!user) return login();
    if (!answers.some((a) => a.trim()) && !writing.trim()) return toast.error("Answer at least one question");
    setSubmitting(true);
    try {
      const r = await api.post(`/coursework/${slug}/submit`, { answers, writing });
      setMySub({ id: r.data.submission_id, answers: [...answers], writing, created_at: new Date().toISOString() });
      setRetake(false);
      api.get(`/coursework/${slug}/my-submission`).then((res) => { if (res.data?.id) setMySub(res.data); }).catch(() => {});
      toast.success("Assignment submitted");
    } catch {
      toast.error("Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  const assign = async () => {
    if (!learnerSel) return toast.error("Choose a learner first");
    const learner = learners.find((l) => l.id === learnerSel);
    setAssigning(true);
    try {
      await api.post("/assignments", { learner_id: learnerSel, item_type: "coursework", slug: item.slug, title: item.title });
      toast.success(`Assigned "${item.title}" to ${learner?.name}`);
    } catch {
      toast.error("Could not assign");
    } finally {
      setAssigning(false);
    }
  };

  const printAnswerKey = async () => {
    let key = answerKey;
    if (!key) {
      try {
        const r = await api.get("/admin/coursework");
        key = r.data.find((c) => c.slug === slug) || null;
        setAnswerKey(key);
      } catch {
        return toast.error("Could not load the answer key");
      }
    }
    if (!key) return toast.error("Answer key not found");
    setPrintingKey(true);
    setTimeout(() => {
      window.print();
      setPrintingKey(false);
    }, 200);
  };

  const printCert = () => {
    setCertOpen(false);
    setPrintingCert(true);
    setTimeout(() => {
      window.print();
      setPrintingCert(false);
    }, 200);
  };

  if (loading) return <div className="min-h-[60vh]"><Spinner /></div>;
  if (!item) return (
    <div className="mx-auto max-w-xl px-6 py-32 text-center">
      <p className="text-muted">Assignment not found.</p>
      <Link to="/assignments" className="mt-4 inline-block text-gold hover:underline">← Back to assignments</Link>
    </div>
  );

  const input = "w-full border border-edge bg-ink px-4 py-3 text-bone placeholder:text-muted focus:border-gold focus:outline-none";
  const showForm = !mySub || retake;

  return (
    <div data-testid="assignment-detail-page" id="lesson-printable" className="mx-auto max-w-3xl px-6 py-12 md:px-12">
      {/* Controls */}
      <div className="no-print">
        <button onClick={() => navigate("/assignments")} className="mb-6 flex items-center gap-2 text-sm text-muted hover:text-gold">
          <ArrowLeft className="h-4 w-4" /> All assignments
        </button>
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <button onClick={() => window.print()} data-testid="print-assignment-btn"
            className="inline-flex items-center gap-2 border border-gold bg-gold/10 px-3 py-2 text-xs font-medium text-gold hover:bg-gold hover:text-ink">
            <Printer className="h-4 w-4" /> Print / PDF worksheet
          </button>
          {user?.is_admin && (
            <button onClick={printAnswerKey} data-testid="print-answer-key-btn"
              className="inline-flex items-center gap-2 border border-edge px-3 py-2 text-xs text-muted hover:border-gold hover:text-gold">
              <KeyRound className="h-4 w-4" /> Print answer key
            </button>
          )}
          {user?.is_admin && scanCount != null && (
            <span data-testid="qr-scan-count" title="Students who scanned the printed worksheet QR"
              className="inline-flex items-center gap-2 border border-edge px-3 py-2 font-mono text-xs text-muted">
              <QrCode className="h-4 w-4 text-gold" /> {scanCount} QR {scanCount === 1 ? "scan" : "scans"}
            </span>
          )}
          {user && learners.length > 0 && (
            <div className="flex items-center gap-2" data-testid="assign-coursework-controls">
              <select value={learnerSel} onChange={(e) => setLearnerSel(e.target.value)} data-testid="assign-learner-select"
                className="border border-edge bg-ink px-3 py-2 text-xs text-bone focus:border-gold focus:outline-none">
                <option value="">Choose a learner…</option>
                {learners.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <button onClick={assign} disabled={assigning} data-testid="assign-coursework-btn"
                className="inline-flex items-center gap-2 border border-edge px-3 py-2 text-xs text-muted hover:border-gold hover:text-gold disabled:opacity-50">
                {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Assign to learner
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Print header */}
      {!printingCert && (
      <div className="print-header hidden">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div>
            <h1 className="font-serif text-3xl font-semibold">{printingKey ? "Answer Key" : "Assignment"} · {item.title}</h1>
            <p className="mt-1 text-sm uppercase tracking-wide">Our African Narratives — Un-erased Black History{printingKey ? " · Educator copy, do not distribute" : ""}</p>
            {!printingKey && <p className="mt-3 text-sm">Name: ____________________________   Date: __________________</p>}
          </div>
          {!printingKey && (
            <div style={{ textAlign: "center", flexShrink: 0 }}>
              <QRCodeSVG value={`${window.location.origin}/assignments/${slug}?src=qr`} size={84} fgColor="#0A0908" bgColor="#ffffff" />
              <p style={{ fontSize: 10, marginTop: 4, color: "#555" }}>Scan to answer online</p>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Screen header */}
      <div className="no-print">
        <SectionLabel>Assignment</SectionLabel>
        <h1 className="font-serif text-4xl font-semibold text-bone lg:text-5xl">{item.title}</h1>
      </div>

      {/* Print-only certificate of achievement */}
      {printingCert && mySub && (
        <div className="print-certificate hidden" data-testid="certificate-print-block">
          <div style={{ border: "4px double #D4AF37", padding: 10 }}>
            <div style={{ border: "1px solid #0A0908", padding: "44px 40px", textAlign: "center" }}>
              <div style={{ display: "flex", height: 6, marginBottom: 28 }}>
                {["#8C1C13", "#D4AF37", "#1E3F20", "#0A0908"].map((c) => <div key={c} style={{ flex: 1, background: c }} />)}
              </div>
              <div style={{ letterSpacing: 4, fontSize: 12, textTransform: "uppercase", color: "#8C1C13", fontWeight: 700 }}>Our African Narratives</div>
              <div className="font-serif" style={{ fontSize: 38, margin: "14px 0 4px", color: "#0A0908", fontWeight: 600 }}>Certificate of Achievement</div>
              <div style={{ color: "#555555", fontSize: 14, fontStyle: "italic" }}>proudly presented to</div>
              <div className="font-serif" style={{ fontSize: 32, color: "#8C1C13", margin: "14px 0" }}>{certName || "Student"}</div>
              <div style={{ color: "#333333", fontSize: 15 }}>for outstanding work on</div>
              <div className="font-serif" style={{ fontSize: 22, color: "#0A0908", margin: "8px 0 18px" }}>{item.title}</div>
              <div style={{ display: "inline-block", border: "2px solid #D4AF37", padding: "8px 20px", fontSize: 18, color: "#0A0908", fontWeight: 700 }}>
                Achieved {mySub.score}%
              </div>
              <div style={{ marginTop: 34, display: "flex", justifyContent: "space-between", fontSize: 13, color: "#555555" }}>
                <span>Date: {new Date(mySub.feedback_at || mySub.created_at || Date.now()).toLocaleDateString()}</span>
                <span>Educator: {mySub.feedback_by || "____________________"}</span>
              </div>
              <div style={{ display: "flex", height: 6, marginTop: 28 }}>
                {["#0A0908", "#1E3F20", "#D4AF37", "#8C1C13"].map((c) => <div key={c} style={{ flex: 1, background: c }} />)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print-only worksheet: always available regardless of submission state */}
      {!printingCert && (
      <div className="print-worksheet hidden">
        {printingKey && answerKey ? (
          <>
            {(answerKey.questions || []).map((q, i) => (
              <div key={i} className="mt-8">
                <p className="ws-text"><span className="mr-2 font-mono text-sm">{i + 1}.</span>{q.question}</p>
                <p className="ws-text mt-2 border-l-2 border-gray-400 pl-3 text-sm"><b>Model answer:</b> {q.model_answer || "—"}</p>
              </div>
            ))}
            {answerKey.writing_task?.prompt && (
              <div className="mt-10 border-t pt-6">
                <p className="ws-text font-semibold">Writing Task: {answerKey.writing_task.prompt}</p>
                {answerKey.writing_task.guidance && (
                  <p className="ws-text mt-2 border-l-2 border-gray-400 pl-3 text-sm"><b>Marking guidance:</b> {answerKey.writing_task.guidance}</p>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {(item.questions || []).map((q, i) => (
              <div key={i} className="mt-8">
                <p className="ws-text"><span className="mr-2 font-mono text-sm">{i + 1}.</span>{q.question}</p>
                <div className="answer-lines">
                  <div className="border-b border-gray-300 py-3" />
                  <div className="border-b border-gray-300 py-3" />
                  <div className="border-b border-gray-300 py-3" />
                </div>
              </div>
            ))}
            {item.writing_task?.prompt && (
              <div className="mt-10 border-t pt-6">
                <p className="ws-text font-semibold">Writing Task: {item.writing_task.prompt}</p>
                {item.writing_task.guidance && <p className="ws-text mt-1 text-sm">{item.writing_task.guidance}</p>}
                <div className="answer-lines">
                  {Array.from({ length: 8 }).map((_, i) => <div key={i} className="border-b border-gray-300 py-3" />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      )}

      {!showForm ? (
        <div className="mt-10 space-y-6 no-print" data-testid="submitted-view">
          <div className="border border-forest/50 bg-ash p-8 text-center" data-testid="submit-success">
            <CheckCircle2 className="mx-auto h-10 w-10 text-[#7bbd7e]" />
            <h2 className="mt-4 font-serif text-2xl text-bone">Submitted — well done!</h2>
            <p className="mt-2 text-muted">Your responses have been recorded{mySub.created_at ? ` on ${new Date(mySub.created_at).toLocaleDateString()}` : ""}.</p>
            {mySub.score != null && mySub.score >= 80 && (
              <button onClick={() => { setCertName(user?.name || ""); setCertOpen(true); }} data-testid="certificate-btn"
                className="mx-auto mt-5 inline-flex items-center gap-2 border border-gold bg-gold/10 px-4 py-2.5 text-sm font-medium text-gold hover:bg-gold hover:text-ink">
                <Award className="h-4 w-4" /> Print certificate of achievement
              </button>
            )}
          </div>

          {mySub.feedback || mySub.score != null ? (
            <div className="border border-gold/50 bg-gold/5 p-6" data-testid="educator-feedback">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-gold">
                  <MessageSquareQuote className="h-4 w-4" /> Educator Feedback{mySub.feedback_by ? ` — ${mySub.feedback_by}` : ""}
                </div>
                {mySub.score != null && (
                  <span className="font-serif text-3xl text-gold" data-testid="my-score">{mySub.score}<span className="text-lg">%</span></span>
                )}
              </div>
              {mySub.feedback && <p className="mt-3 whitespace-pre-line leading-relaxed text-[#e8e3d8]">{mySub.feedback}</p>}
            </div>
          ) : (
            <p className="border border-dashed border-edge p-5 text-center text-sm text-muted" data-testid="no-feedback-yet">
              No educator feedback yet — check back here soon.
            </p>
          )}

          <div>
            <h3 className="mb-4 font-serif text-2xl text-bone">Your responses</h3>
            <div className="space-y-5">
              {(item.questions || []).map((q, i) => (
                <div key={i} className="border-l-2 border-edge pl-4" data-testid={`my-answer-${i}`}>
                  <p className="text-bone"><span className="mr-2 font-mono text-sm text-gold">{i + 1}.</span>{q.question}</p>
                  <p className="mt-1 text-sm text-[#cfc9bd]">{(mySub.answers?.[i] || "").trim() || "—"}</p>
                </div>
              ))}
              {mySub.writing && item.writing_task?.prompt && (
                <div className="border-l-2 border-gold/50 pl-4" data-testid="my-writing">
                  <p className="text-bone"><PenLine className="mr-2 inline h-4 w-4 text-gold" />{item.writing_task.prompt}</p>
                  <p className="mt-1 whitespace-pre-line text-sm text-[#cfc9bd]">{mySub.writing}</p>
                </div>
              )}
            </div>
          </div>

          <button onClick={() => setRetake(true)} data-testid="retake-btn" className="flex items-center gap-2 text-sm text-muted hover:text-gold">
            <RotateCcw className="h-4 w-4" /> Start a fresh attempt
          </button>
        </div>
      ) : (
        <div className="no-print mt-10 space-y-8">
          {(item.questions || []).map((q, i) => (
            <div key={i} data-testid={`answer-block-${i}`}>
              <p className="mb-2 flex items-start gap-2 text-bone">
                <span className="font-mono text-sm text-gold">{i + 1}.</span>
                <span className="text-lg">{q.question}</span>
              </p>
              <textarea value={answers[i] || ""} onChange={(e) => setAnswers((a) => a.map((x, idx) => (idx === i ? e.target.value : x)))}
                rows={3} placeholder="Your answer…" data-testid={`answer-input-${i}`} className={input} />
            </div>
          ))}

          {item.writing_task?.prompt && (
            <div data-testid="writing-block" className="border-t border-edge pt-8">
              <div className="mb-2 flex items-center gap-2">
                <Pill tone="gold"><PenLine className="mr-1 inline h-3 w-3" /> Writing Task</Pill>
              </div>
              <p className="text-lg text-bone">{item.writing_task.prompt}</p>
              {item.writing_task.guidance && <p className="mt-1 text-sm text-muted">{item.writing_task.guidance}</p>}
              <textarea value={writing} onChange={(e) => setWriting(e.target.value)} rows={7} placeholder="Write your response…"
                data-testid="writing-input" className={`${input} mt-3`} />
            </div>
          )}

          <div className="space-y-4">
            {!user && <p className="text-sm text-muted">You'll be asked to sign in when you submit.</p>}
            <Button variant="gold" onClick={submit} disabled={submitting} className="w-full" data-testid="submit-assignment-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Submit Assignment
            </Button>
          </div>
        </div>
      )}

      {!printingCert && (
      <div className="print-footer hidden">
        <p className="mt-10 border-t pt-3 text-xs">Generated by Our African Narratives — Un-erased Black History · {window.location.origin}</p>
      </div>
      )}

      {certOpen && (
        <div className="no-print fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => setCertOpen(false)} data-testid="certificate-modal">
          <div className="w-full max-w-md border border-edge bg-ink p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-serif text-2xl text-bone"><Award className="h-5 w-5 text-gold" /> Certificate of Achievement</h3>
              <button onClick={() => setCertOpen(false)} className="text-muted hover:text-bone"><X className="h-5 w-5" /></button>
            </div>
            <label className="mb-1 block font-mono text-[11px] uppercase tracking-[0.15em] text-muted">Name on the certificate</label>
            <input value={certName} onChange={(e) => setCertName(e.target.value)} data-testid="certificate-name-input"
              placeholder="e.g. Amara Carter"
              className="mb-4 w-full border border-edge bg-ash px-4 py-3 text-bone placeholder:text-muted focus:border-gold focus:outline-none" />
            <Button variant="gold" className="w-full" onClick={printCert} data-testid="certificate-print-btn">
              <Printer className="h-4 w-4" /> Print certificate
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
